import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Instagram, Globe, Film, ExternalLink } from 'lucide-react';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TalentProfilePage() {
  const { id } = useParams();
  const [talent, setTalent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchTalent();
  }, [id]);

  const fetchTalent = async () => {
    try {
      const response = await axios.get(`${API}/talents/public/${id}`);
      setTalent(response.data);
    } catch (err) {
      setError('Talent not found');
    } finally {
      setLoading(false);
    }
  };

  const defaultImage = "https://images.unsplash.com/photo-1622169804256-0eb6873ff441?w=600&h=800&fit=crop";

  if (loading) {
    return (
      <div>
        <Header />
        <div className="talent-profile section">
          <div className="container loading">
            <div className="spinner" />
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !talent) {
    return (
      <div>
        <Header />
        <div className="talent-profile section">
          <div className="container">
            <div className="empty-state" data-testid="talent-not-found">
              <h3>Talent Not Found</h3>
              <p>The profile you're looking for doesn't exist or has been removed.</p>
              <Link to="/roster" className="btn btn-primary" style={{ marginTop: '1rem' }}>
                Back to Roster
              </Link>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div>
      <Header />
      
      <section className="talent-profile section section-dark" data-testid="talent-profile">
        <div className="container">
          <Link to="/roster" className="btn btn-ghost" style={{ marginBottom: '2rem' }} data-testid="back-to-roster">
            <ArrowLeft size={18} />
            Back to Roster
          </Link>

          <div className="talent-profile-header">
            <motion.div 
              className="talent-profile-image"
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <img 
                src={talent.headshot_url || defaultImage} 
                alt={talent.name}
                onError={(e) => { e.target.src = defaultImage; }}
              />
            </motion.div>

            <motion.div 
              className="talent-profile-info"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 data-testid="talent-name">{talent.name}</h1>
              
              <div className="talent-profile-meta">
                {talent.representation_type && (
                  <span>{talent.representation_type}</span>
                )}
                {talent.age_range && (
                  <span>Age Range: {talent.age_range}</span>
                )}
                {talent.height && (
                  <span>Height: {talent.height}</span>
                )}
              </div>

              {talent.bio && (
                <p className="talent-profile-bio" data-testid="talent-bio">{talent.bio}</p>
              )}

              {talent.skills && talent.skills.length > 0 && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <h4 style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>
                    Skills & Specialties
                  </h4>
                  <div className="talent-card-skills">
                    {talent.skills.map((skill, idx) => (
                      <span key={idx} className="skill-tag">{skill}</span>
                    ))}
                  </div>
                </div>
              )}

              <div className="talent-profile-links">
                {talent.demo_reel_url && (
                  <a 
                    href={talent.demo_reel_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn btn-secondary"
                    data-testid="demo-reel-link"
                  >
                    <Film size={18} />
                    Demo Reel
                  </a>
                )}
                {talent.instagram && (
                  <a 
                    href={talent.instagram.startsWith('http') ? talent.instagram : `https://instagram.com/${talent.instagram}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="social-link"
                    data-testid="instagram-link"
                    title="Instagram"
                  >
                    <Instagram size={20} />
                  </a>
                )}
                {talent.imdb && (
                  <a 
                    href={talent.imdb} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="social-link"
                    data-testid="imdb-link"
                    title="IMDb"
                  >
                    <ExternalLink size={20} />
                  </a>
                )}
                {talent.website && (
                  <a 
                    href={talent.website} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="social-link"
                    data-testid="website-link"
                    title="Website"
                  >
                    <Globe size={20} />
                  </a>
                )}
              </div>
            </motion.div>
          </div>

          {/* Contact CTA */}
          <motion.div 
            style={{ 
              marginTop: '4rem', 
              padding: '3rem', 
              backgroundColor: 'var(--surface)', 
              borderRadius: '0.5rem',
              textAlign: 'center'
            }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <h3 style={{ marginBottom: '1rem' }}>Interested in {talent.name}?</h3>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
              Contact Woodhouse & Archer for bookings and availability.
            </p>
            <Link to="/contact" className="btn btn-primary" data-testid="contact-about-talent">
              Get in Touch
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
